import Vue from 'vue';
import { kebabCase } from 'lodash';

// PLUGINS
import VueCompositionAPI from '@vue/composition-api';
import VueScrollTo from 'vue-scrollto';
import VueMeta from 'vue-meta';
import Sticky from 'vue-sticky-directive';

// COMPONENTS
import {
  ValidationProvider,
  ValidationObserver,
} from 'vee-validate';

import ValidationObserverEventEmitter from '@/views/LG/ValidationObserverEventEmitter';

import BrandingLogo from '@/components/BrandingLogo/BrandingLogo.vue';
import BespokeFooterInformation from '@/components/BespokeFooterInformation/BespokeFooterInformation.vue';
import Checkbox from '@/components/Checkbox/Checkbox.vue';
import CheckboxSquare from '@/components/Checkbox/CheckboxSquare.vue';
import Radio from '@/components/Radio/Radio.vue';
import Box from '@/components/Box/Box.vue';
import Icon from '@/components/Icon/Icon.vue';
import FormRow from '@/components/FormRow/FormRow.vue';
import FormSection from '@/components/FormSection/FormSection.vue';
import FormSectionHighlight from '@/components/FormSection/FormSectionHighlight.vue';
import ShowMore from '@/components/car/Common/ShowMore.vue';
import FormSectionLabel from '@/components/car/FormSection/FormSectionLabel.vue';
import FormSectionQuestion from '@/components/car/FormSection/FormSectionQuestion.vue';
import StepErrors from '@/components/StepErrors/StepErrors.vue';
import HelpText from '@/components/HelpText/HelpText';
import HelpTextController from '@/components/HelpText/HelpTextController';
import HelpTextContent from '@/components/HelpText/HelpTextContent';
import Quantity from '@/components/Quantity/Quantity';
import ContentsCalculator from '@/components/home/ContentsCalculator/ContentsCalculator';
import QzNumberInput from '@/components/QzNumberInput/QzNumberInput';
import CurrencyInput from '@/components/CurrencyInput/CurrencyInput';
import QzDateSelect from '@/components/QzDateSelect/QzDateSelect';
import QzDateInput from '@/components/QzDateSelect/QzDateInput';
import QzAutocomplete from '@/components/Autocomplete/Autocomplete';
import QzSelect from '@/components/QzSelect.vue';
import QzTextField from '@/components/QzTextField.vue';
import QzTextarea from '@/components/QzTextarea.vue';
import QzButton from '@/components/QzButton.vue';
import QuoteLimitExceedPopup from '@/components/QuoteLimitExceedPopup.vue';
import PageLoader from '@/components/PageLoader/PageLoader';
import { VTextField } from 'vuetify/lib';
import TextInput from '@/components/TextInput/TextInput';
import CheckboxButton from '@/components/Checkbox/CheckboxButton';
import CustomValidationObserver from '@/components/shared/CustomValidationObserver';

// DIRECTIVES
import {
  scrollToViewport,
} from '@/directives/v-scroll-to-viewport';
import {
  addZukoTag,
} from '@/directives/v-add-zuko-tag';

import {
  getPartnerHeaderComponent,
  getPartnerFooterComponent,
} from '@/helpers/common';
Vue.component('validation-observer-event-emitter', ValidationObserverEventEmitter);

export default (config, productName) => {
  Radio.props.outlined.default = !!config.partnerRadioOutlined;
  HelpText.props.plain.default = config.helptextPlainBg;
  FormRow.props.isVerticalSplitted.default = config.isVerticalSplitted;

  if (['home', 'travel', 'breakdown'].includes(productName)) {
    Radio.props.wide.default = true;
  }

  Vue.use(VueCompositionAPI);
  Vue.use(VueScrollTo, {
    duration: 300,
  });
  Vue.use(VueMeta);
  Vue.use(Sticky);

  const siteHeader = getPartnerHeaderComponent(config, productName);
  const siteFooter = getPartnerFooterComponent(config, productName);

  Vue.component('site-header', siteHeader);
  Vue.component('site-footer', siteFooter);

  Vue.component('branding-logo', BrandingLogo);
  Vue.component('bespoke-footer-information', BespokeFooterInformation);
  Vue.component('checkbox-square', CheckboxSquare);
  Vue.component('checkbox-button', CheckboxButton);
  Vue.component('custom-validation-observer', CustomValidationObserver);
  Vue.component('radio', Radio);
  Vue.component('text-input', TextInput);
  Vue.component('box', Box);
  Vue.component('icon', Icon);
  Vue.component('form-row', FormRow);
  Vue.component('form-section', FormSection);
  Vue.component('form-section-highlight', FormSectionHighlight);
  Vue.component('form-section-label', FormSectionLabel);
  Vue.component('form-section-question', FormSectionQuestion);
  Vue.component('checkbox', Checkbox);
  Vue.component('show-more', ShowMore);
  Vue.component('step-errors', StepErrors);
  Vue.component('help-text', HelpText);
  Vue.component('help-text-controller', HelpTextController);
  Vue.component('help-text-content', HelpTextContent);
  Vue.component('quantity', Quantity);
  Vue.component('contents-calculator', ContentsCalculator);
  Vue.component('currency-input', CurrencyInput);
  Vue.component('qz-date-select', QzDateSelect);
  Vue.component('qz-date-input', QzDateInput);
  Vue.component('qz-autocomplete', QzAutocomplete);
  Vue.component('qz-number-input', QzNumberInput);
  Vue.component('qz-select', QzSelect);
  Vue.component('qz-text-field', QzTextField);
  Vue.component('qz-textarea', QzTextarea);
  Vue.component('qz-btn', QzButton);
  Vue.component('quote-limit-exceed-popup', QuoteLimitExceedPopup);
  Vue.component('page-loader', PageLoader);
  Vue.component('validation-provider', ValidationProvider);
  Vue.component('validation-observer', ValidationObserver);

  // v-text-field is used by VCurrencyField and must be register globally.
  // With enabled treeshaking, webpack don't recognize v-text-field inside v-currency-field component
  Vue.component('v-text-field', VTextField);

  Vue.directive('scroll-to-viewport', scrollToViewport);
  Vue.directive('add-zuko-tag', addZukoTag);

  const QuestionsContext = require.context('@/components/questions/', true, /\.vue$/i, 'eager');

  QuestionsContext.keys().forEach((componentFilePath) => {
    const questionComponentName = (() => {
      const parts = componentFilePath.split('/');
      return kebabCase(parts[parts.length - 1].split('.')[0]);
    })();
    Vue.component(`question-${questionComponentName}`, () => QuestionsContext(componentFilePath));
  });
};
